import type { ReactNode } from 'react';
import { PermissionDenied } from '@bb/ui';
import { usePermissions, type PermissionScope } from './usePermissions.ts';

interface Props {
  permission: string;
  scope?: PermissionScope;
  children: ReactNode;
  fallback?: ReactNode;
}

/** Renders its children only when the employee holds `permission` (hides actions that the API would refuse). */
export function Can({ permission, scope = 'environment', children, fallback = null }: Props) {
  const { has } = usePermissions(scope);
  return <>{has(permission) ? children : fallback}</>;
}

/** Page guard: a direct link to a page the employee cannot use shows which permission is missing. */
export function RequirePermission({ permission, scope = 'environment', children }: Omit<Props, 'fallback'>) {
  const { isPending, has } = usePermissions(scope);
  if (isPending) return <p aria-busy="true">Checking access…</p>;
  return <>{has(permission) ? children : <PermissionDenied permission={permission} />}</>;
}
